-- | Effect runners for app operations that have an OperationId.
module App.Operation.Runner
    ( runStep1InitialLoad
    , runStep1Search
    , runStep1PageChange
    , runStep1CurrentPageReload
    , runStep1SaveFormOrder
    , runStep1ToggleLocks
    , runStep1CopyDocuments
    , runStep1DeleteDocuments
    , runStep1ExportDocuments
    , runStep1RequestSignatures
    , runStep1UpdateForms
    , runStep2AInitialLoad
    , runStep2BLoad
    , runStep2ExportAll
    , runStep3FormTypesLoad
    , runStep3InitialLoad
    , runSaveStep3
    , runLoadStep3
    , runAutofillStep3
    , runToggleStep3Lock
    , runStep3DocumentMutation
    ) where

import API.Attachments (AttachmentParent(..), deleteAttachment, postAttachment)
import API.Autofill (AutofillArgs, autofillData)
import API.Comments (CommentParent(..), deleteComment, postComment, updateComment)
import API.Document (CopyDocRequest, ToggleLockResult, copyDocuments, deleteDocuments, exportDocuments, toggleDocumentLocks)
import API.Pagination (changePage, pageNumber)
import API.RecentForms (searchRecentForms)
import API.SelStudent (searchStudents)
import API.Signature (SignResult, SignatureRequest, fetchSignatureRefresh, requestSigs, signForm, signWithSaved)
import API.Types (DocumentID)
import App.Msg (Msg(..))
import App.Operation.Types (OperationId, OperationKind(..))
import App.State (AppState, Step3Init, Step3Session(..))
import Common
import Control.Monad.Except (except, runExceptT)
import Data.Array as Arr
import Data.Maybe (isNothing)
import Effect.Aff (launchAff_)
import Form.StudentSelect as StudentSelect
import GraphQL.Join (getDecode, getQuery, runGraphQL, runMultiQuery, runMultiQueryWithUpload)
import GraphQL.Query (Query, queryUploads)
import Steps.FormPicker as FormPicker
import Steps.Step1.State (InitialState, RecentFormState, initRecentFormState, rfSearchQuery, selectedDocumentIds, selectedForms) as Step1
import Steps.Step1.Update (DocExport, UpdateFormsSelection, runUpdateFormsMutation, toggleLockRequests) as Step1
import Steps.Step3.State as Step3State
import Steps.Step3.Transition (PendingTransition)
import Steps.Step3.Types as Step3Types
import Steps.Step3.Update as Step3Update
import Steps.WorkQueue.Cache (WorkQueueCache)
import Steps.WorkQueue.State (WorkQueueState, createAllForms)
import Steps.WorkQueue.UIState (WorkQueueUiState)
import Widgets.Search (stateToQuery)

type Enqueue =
    Array Msg -> Effect Unit

-- Each runner records the operation before it starts async work. It then sends
-- the result messages back through the same app mailbox that user events use.
runStep1InitialLoad :: Enqueue -> OperationId -> Step1.InitialState -> Effect Unit
runStep1InitialLoad enqueue opId initial = do
    enqueue [ OpStarted opId LoadRecentForms Nothing ]
    launchAff_ do
        result' <- Step1.initRecentFormState initial
        liftEffect $ enqueue $ operationMessages opId result' [ Step1InitialLoaded opId result' ]

runStep1Search :: Enqueue -> OperationId -> Step1.RecentFormState -> Effect Unit
runStep1Search enqueue opId ready = do
    enqueue [ OpStarted opId LoadRecentForms Nothing ]
    launchAff_ do
        result' <- runGraphQL $ searchRecentForms $ Step1.rfSearchQuery ready
        liftEffect $ enqueue $ operationMessages opId result' [ Step1RecentFormsLoaded opId result' ]

runStep1PageChange :: Enqueue -> OperationId -> Step1.RecentFormState -> Int -> Effect Unit
runStep1PageChange enqueue opId ready idx = do
    enqueue [ OpStarted opId LoadRecentForms Nothing ]
    launchAff_ do
        result' <- changePage idx ready.recentForms
        liftEffect $ enqueue $ operationMessages opId result' [ Step1RecentFormsLoaded opId result' ]

runStep1CurrentPageReload :: Enqueue -> OperationId -> Step1.RecentFormState -> Effect Unit
runStep1CurrentPageReload enqueue opId ready = do
    enqueue [ OpStarted opId LoadRecentForms Nothing ]
    launchAff_ do
        result' <- changePage (pageNumber ready.recentForms) ready.recentForms
        liftEffect $ enqueue $ operationMessages opId result' [ Step1RecentFormsLoaded opId result' ]

runStep1SaveFormOrder :: Enqueue -> OperationId -> Step1.RecentFormState -> Effect Unit
runStep1SaveFormOrder enqueue opId ready = do
    enqueue [ OpStarted opId SaveStep1FormOrder Nothing ]
    launchAff_ do
        result' <- FormPicker.endFormReorder ready.formPicker
        liftEffect $ enqueue $ operationMessages opId result' [ Step1FormOrderSaved opId result' ]

runStep1ToggleLocks :: Enqueue -> OperationId -> Step1.RecentFormState -> Effect Unit
runStep1ToggleLocks enqueue opId ready = do
    enqueue [ OpStarted opId (BulkToggleDocumentLocks $ Step1.selectedDocumentIds ready) Nothing ]
    launchAff_ do
        result' <- case Step1.toggleLockRequests ready of
            Nothing -> pure $ Left $ StringError "Step 1 lock toggle requested without lockable selected documents."
            Just requests -> runGraphQL $ toggleDocumentLocks requests
        liftEffect $ enqueue $ operationMessages opId result' [ Step1LocksToggled opId result' ]

runStep1CopyDocuments :: Enqueue -> OperationId -> Step1.RecentFormState -> Array CopyDocRequest -> Effect Unit
runStep1CopyDocuments enqueue opId ready requests = do
    enqueue [ OpStarted opId (CopyRecentForms $ Step1.selectedDocumentIds ready) Nothing ]
    launchAff_ do
        result' <- runGraphQL $ copyDocuments requests
        liftEffect $ enqueue $ operationMessages opId result' [ Step1DocumentsCopied opId result' ]

runStep1DeleteDocuments :: Enqueue -> OperationId -> Step1.RecentFormState -> Effect Unit
runStep1DeleteDocuments enqueue opId ready = do
    enqueue [ OpStarted opId (DeleteRecentForms $ Step1.selectedDocumentIds ready) Nothing ]
    launchAff_ do
        result' <- runGraphQL $ deleteDocuments $ Step1.selectedDocumentIds ready
        liftEffect $ enqueue $ operationMessages opId result' [ Step1DocumentsDeleted opId result' ]

runStep1ExportDocuments :: Enqueue -> OperationId -> Step1.RecentFormState -> Step1.DocExport -> Effect Unit
runStep1ExportDocuments enqueue opId ready etype = do
    enqueue [ OpStarted opId (ExportRecentForms $ Step1.selectedDocumentIds ready) Nothing ]
    launchAff_ do
        result' <- runGraphQL $ exportDocuments $ Step1.selectedDocumentIds ready
        liftEffect $ enqueue $ operationMessages opId result' [ Step1DocumentsExported opId etype result' ]

runStep1RequestSignatures :: Enqueue -> OperationId -> Array SignatureRequest -> Effect Unit
runStep1RequestSignatures enqueue opId requests = do
    enqueue [ OpStarted opId (RequestRecentFormSignatures $ map _.docId requests) Nothing ]
    launchAff_ do
        result' <- runGraphQL $ requestSigs requests
        liftEffect $ enqueue $ operationMessages opId result' [ Step1SignaturesRequested opId result' ]

runStep1UpdateForms :: Enqueue -> OperationId -> Step1.RecentFormState -> Step1.UpdateFormsSelection -> Effect Unit
runStep1UpdateForms enqueue opId ready selection = do
    enqueue [ OpStarted opId (UpdateRecentForms $ Step1.selectedDocumentIds ready) Nothing ]
    launchAff_ do
        result' <- runGraphQL $ Step1.runUpdateFormsMutation selection $ Step1.selectedForms ready
        liftEffect $ enqueue $ operationMessages opId result' [ Step1FormsUpdated opId selection result' ]

runStep2AInitialLoad :: Enqueue -> OperationId -> WorkQueueState -> WorkQueueCache -> Effect Unit
runStep2AInitialLoad enqueue opId workQueueState workQueueCache = do
    enqueue [ OpStarted opId LoadStep2Students Nothing ]
    launchAff_ do
        let selectState = StudentSelect.initState workQueueState workQueueCache
        result' <- map (\page -> StudentSelect.updatePage page selectState) <$> runGraphQL (searchStudents $ stateToQuery selectState.search)
        liftEffect $ enqueue $ operationMessages opId result' [ Step2AInitialLoaded opId result' ]

runStep2BLoad :: Enqueue -> OperationId -> WorkQueueState -> WorkQueueUiState -> WorkQueueCache -> Effect Unit
runStep2BLoad enqueue opId workQueueState workQueueUi workQueueCache = do
    enqueue [ OpStarted opId LoadStep2FormPicker Nothing ]
    launchAff_ do
        result' <- runGraphQL $ FormPicker.loadFormPicker workQueueState workQueueUi workQueueCache
        liftEffect $ enqueue $ operationMessages opId result' [ Step2BLoaded opId result' ]

runStep2ExportAll :: Enqueue -> OperationId -> WorkQueueState -> WorkQueueCache -> Effect Unit
runStep2ExportAll enqueue opId workQueueState workQueueCache = do
    enqueue [ OpStarted opId ExportStep2Forms Nothing ]
    launchAff_ do
        result' <- createAllForms workQueueState (map _.value workQueueCache.defTeacher) (map _.value workQueueCache.defSemester)
        liftEffect $ enqueue $ operationMessages opId result' [ Step2FormsExported opId result' ]

runStep3FormTypesLoad :: Enqueue -> AppState -> OperationId -> Step3Init -> Effect Unit
runStep3FormTypesLoad enqueue state opId init = do
    enqueue [ OpStarted opId LoadStep3FormTypes Nothing ]
    launchAff_ do
        result' <- Step3State.preloadStep3Resources state.workQueueState state.workQueueCache init.formType init.doc
        liftEffect $ enqueue $ operationMessages opId result' [ Step3FormTypesLoaded opId result' ]

runStep3InitialLoad :: Enqueue -> AppState -> OperationId -> OperationKind -> Step3Init -> Effect Unit
runStep3InitialLoad enqueue state opId kind init = do
    enqueue [ OpStarted opId kind Nothing ]
    launchAff_ do
        result' <- Step3State.loadInitialStep3State
            state.workQueueState
            state.workQueueUi
            state.workQueueCache
            init.doc
            init.initialAnchor
        liftEffect $ enqueue $ operationMessages opId result' [ Step3InitialLoaded opId result' ]

runSaveStep3 :: Enqueue -> AppState -> OperationId -> OperationKind -> Maybe Int -> Effect Unit
runSaveStep3 enqueue state opId kind generation = do
    enqueue [ OpStarted opId kind generation ]
    launchAff_ do
        result' <- case state.step3 of
            Step3Ready ready -> Step3State.saveCurrentDocument ready
            _ -> pure $ Left $ StringError "Step 3 save requested without a ready Step 3 session."
        liftEffect $ enqueue $ operationMessages opId result'
            [ Step3Msg $ Step3Update.SaveCompleted opId result' ]

runLoadStep3 :: Enqueue -> AppState -> OperationId -> OperationKind -> PendingTransition -> Effect Unit
runLoadStep3 enqueue state opId kind target = do
    enqueue [ OpStarted opId kind (Just target.generation) ]
    launchAff_ do
        result' <- case state.step3 of
            Step3Ready ready -> Step3State.loadTargetStep3State ready Nothing
            _ -> pure $ Left $ StringError "Step 3 load requested without a ready Step 3 session."
        let editorResult = map _.editor result'
        liftEffect $ enqueue $ operationMessages opId result'
            [ Step3Msg $ Step3Update.LoadCompleted opId target.generation editorResult ]

runAutofillStep3 :: Enqueue -> OperationId -> OperationKind -> AutofillArgs -> Effect Unit
runAutofillStep3 enqueue opId kind args = do
    enqueue [ OpStarted opId kind Nothing ]
    launchAff_ do
        result' <- runGraphQL $ autofillData args
        liftEffect $ enqueue $ operationMessages opId result'
            [ Step3Msg $ Step3Update.AutofillCompleted opId result' ]

runToggleStep3Lock :: Enqueue -> AppState -> OperationId -> OperationKind -> Effect Unit
runToggleStep3Lock enqueue state opId kind = do
    enqueue [ OpStarted opId kind Nothing ]
    launchAff_ do
        result' <- case state.step3 of
            Step3Ready ready -> do
                let requests =
                        [ { form_id: ready.editor.fillState.doc.id
                          , locked: isNothing ready.editor.fillState.doc.locked_at
                          }
                        ]
                results <- runGraphQL $ toggleDocumentLocks requests
                pure $ results >>= firstLockResult
            _ ->
                pure $ Left $ StringError "Step 3 lock toggle requested without a ready Step 3 session."
        liftEffect $ enqueue $ operationMessages opId result'
            [ Step3Msg $ Step3Update.LockCompleted opId result' ]

runStep3DocumentMutation :: Enqueue -> OperationId -> OperationKind -> Step3Types.DocumentMutationRequest -> Effect Unit
runStep3DocumentMutation enqueue opId kind request = do
    enqueue [ OpStarted opId kind Nothing ]
    launchAff_ do
        result' <- runStep3DocumentMutationRequest request
        liftEffect $ enqueue $ operationMessages opId result'
            [ Step3Msg $ Step3Update.DocumentMutationCompleted opId result' ]

runStep3DocumentMutationRequest :: Step3Types.DocumentMutationRequest -> Aff (Either ErrorWrapper Step3Types.DocumentMutationResult)
runStep3DocumentMutationRequest = case _ of
    Step3Types.CreateComment docId input ->
        map Step3Types.CommentCreated <$> runGraphQL (postComment (FormComment docId) input)

    Step3Types.UpdateComment _ commentId input ->
        map Step3Types.CommentUpdated <$> runGraphQL (updateComment commentId input)

    Step3Types.DeleteComment _ commentId ->
        map (Step3Types.CommentDeleted <<< _.id) <$> runGraphQL (deleteComment commentId)

    Step3Types.CreateAttachment docId input ->
        map Step3Types.AttachmentCreated <$> runGraphQL (postAttachment (FormAttachment docId) input)

    Step3Types.DeleteAttachment _ attachmentId ->
        map (Step3Types.AttachmentDeleted <<< _.id) <$> runGraphQL (deleteAttachment attachmentId)

    Step3Types.SignWithCapturedSignature docId field sig opts ->
        runSignAndRefresh docId $ signForm docId field sig opts

    Step3Types.SignWithSavedSignature docId field ->
        runSignAndRefresh docId $ signWithSaved docId field

    Step3Types.RefreshSignatureGroup docId ->
        map Step3Types.SignatureGroupRefreshed <$> runGraphQL (fetchSignatureRefresh docId)

    Step3Types.RequestSignatures requests ->
        map Step3Types.SignaturesRequested <$> runGraphQL (requestSigs requests)

runSignAndRefresh :: DocumentID -> Query SignResult -> Aff (Either ErrorWrapper Step3Types.DocumentMutationResult)
runSignAndRefresh docId signQuery = runExceptT do
    let refreshQuery = fetchSignatureRefresh docId
        -- Checked with Josh about backend's behaviour, and this will not
        -- introduce a race condition; these queries will be run in series
        -- and not in parallel.
        queries = [ getQuery signQuery, getQuery refreshQuery ]
        uploads = queryUploads signQuery
    responses <- case Arr.length uploads of
        0 ->
            runMultiQuery queries
        1 -> case Arr.index uploads 0 of
            Just upload ->
                -- Batched multipart uploads include the operation index before
                -- the normal variable path.
                runMultiQueryWithUpload queries upload { path = "0.variables." <> upload.path }
            Nothing ->
                except $ Left $ StringError "Expected one signature upload, but none was available."
        _ ->
            except $ Left $ StringError "Multiple file uploads are not currently supported"
    signatureJson <- except $ multiResponseAt "signForm" 0 responses
    refreshJson <- except $ multiResponseAt "GetDocument" 1 responses
    signature <- except $ getDecode signQuery signatureJson
    refresh <- except $ getDecode refreshQuery refreshJson
    pure $ Step3Types.SignatureSignedAndRefreshed signature refresh

multiResponseAt :: String -> Int -> Array Json -> Either ErrorWrapper Json
multiResponseAt operation idx responses = case Arr.index responses idx of
    Just json -> Right json
    Nothing -> Left $ StringError $ operation <> " was missing from the batched GraphQL response."

operationMessages :: forall a. OperationId -> Either ErrorWrapper a -> Array Msg -> Array Msg
operationMessages opId result payload =
    payload <> [ statusMessage ]
  where
    statusMessage = case result of
        Left err -> OpFailed opId err
        Right _ -> OpCompleted opId

firstLockResult :: Array ToggleLockResult -> Either ErrorWrapper ToggleLockResult
firstLockResult results = case Arr.index results 0 of
    Just result -> Right result
    Nothing -> Left $ StringError "ToggleDocumentLocks returned no updated documents."
